import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  User,
  Calendar,
  Mail,
  Trophy,
  Bookmark,
  BookOpen,
  Link as LinkIcon,
  Image as ImageIcon,
  Pencil,
  Eye,
  FileText,
  X
} from "lucide-react";
import { useAuth } from "../../services/AuthContext.js";
import { AccountSpecificMessage } from "../../components/EnrollButton.js";
import {
  REFUND_DAYS,
  canHaveRenewal,
  getCycleLabel as getConfigCycleLabel,
} from "../../config/subscription.js";
import "./account.css";

const baseAPI_URL = process.env.REACT_APP_BACKEND_API_BASE_ENDPOINT;

function Account() {
  const { user, getIdToken } = useAuth();

  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [cancellationModal, setCancellationModal] = useState({
    isOpen: false,
    subscriptionIndex: null,
    selectedType: null,
    isSubmitting: false,
    response: null,
    responseStatus: null
  });
  const default_profile_image_url = "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";

  const userId = user?.cognitoUser?.username;

  useEffect(() => {
    if (!userId) return;

    let isMounted = true;

    const fetchAccount = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = await getIdToken();
        const response = await fetch(`${baseAPI_URL}/users/get-account-info`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await response.json();

        if (isMounted) {
          if (data?.error) {
            setError(data.error);
          } else {
            setAccount(data);
          }
        }
      } catch (err) {
        console.error("Erro ao carregar conta:", err);
        if (isMounted) setError("Não foi possível carregar as informações da conta");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAccount();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    setImageError(false);
  }, [account]);

  const formatDate = (date) => {
    if (!date) return "—";
    return new Date(date).toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const formatBRL = (value) => {
    if (value === null || value === undefined) return "—";

    return Number(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL"
    });
  };

  const getSubscriptionStatusLabel = (status) => {
    const labels = {
      ACTIVE: "Ativa",
      CANCELLED: "Cancelada",
      EXPIRED: "Expirada"
    };

    return labels[status] || status;
  };

  const getPaymentStatusLabel = (status) => {
    const labels = {
      RECEIVED: "Recebido",
      CONFIRMED: "Confirmado",
      PENDING: "Pendente",
      REFUNDED: "Reembolsado",
      OVERDUE: "Vencido"
    };

    return labels[status] || status;
  };

  const getCycleLabel = (cycle) => {
    if (cycle === "UNIQUE") return "Único";
    return getConfigCycleLabel(cycle);
  };

  const getBillingTypeLabel = (billingType) => {
    const labels = {
      PIX: "PIX",
      CREDIT_CARD: "Cartão de crédito",
      OTHER: "Outro"
    };

    return labels[billingType] || billingType;
  };

  const countCompletedLessons = (completedLessons) => {
    if (!completedLessons || typeof completedLessons !== "object") {
      return 0;
    }

    let count = 0;
    const traverse = (obj) => {
      for (const key in obj) {
        if (obj[key] === true) {
          count++;
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
          traverse(obj[key]);
        }
      }
    };

    traverse(completedLessons);
    return count;
  };

  const isSubscriptionWithinRefundWindow = (createdAt) => {
    if (!createdAt) return false;
    const createdDate = new Date(createdAt);
    const now = new Date();
    const differenceInMs = now - createdDate;
    const differenceInDays = differenceInMs / (1000 * 60 * 60 * 24);
    return differenceInDays < REFUND_DAYS;
  };

  const canCancelWithRefund = (subscription) => {
    return subscription.status === "ACTIVE" && isSubscriptionWithinRefundWindow(subscription.created_at);
  };

  const canCancelRenewal = (subscription) => {
    if (subscription.status !== "ACTIVE") return false;
    if (subscription.cycle === "UNIQUE") return false;
    if (!canHaveRenewal(subscription.cycle)) return false;
    if (subscription.billing_type === "CREDIT_CARD") {
      const firstPayment = subscription.payments?.[0];
      if (firstPayment && firstPayment.num_insts > 1) return false;
    }
    return true;
  };

  const openCancellationModal = (subscriptionIndex) => {
    setCancellationModal({
      isOpen: true,
      subscriptionIndex,
      selectedType: null,
      isSubmitting: false
    });
  };

  const closeCancellationModal = () => {
    setCancellationModal({
      isOpen: false,
      subscriptionIndex: null,
      selectedType: null,
      isSubmitting: false,
      response: null,
      responseStatus: null
    });
  };

  const handleCancellationTypeChange = (type) => {
    setCancellationModal(prev => ({
      ...prev,
      selectedType: type
    }));
  };

  const handleCancelSubscription = async () => {
    const { subscriptionIndex, selectedType } = cancellationModal;
    if (!selectedType || subscriptionIndex === null) return;

    const subscription = account.subscriptions[subscriptionIndex];

    setCancellationModal(prev => ({
      ...prev,
      isSubmitting: true
    }));

    try {
      const token = await getIdToken();
      const endpoint = `${baseAPI_URL}/new_subscriptions/${subscription.id}/cancel`;

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          cancellation_type: selectedType
        })
      });

      const data = await response.json();

      if (response.ok) {
        setCancellationModal(prev => ({
          ...prev,
          isSubmitting: false,
          response: data.message,
          responseStatus: "success"
        }));
      } else {
        setCancellationModal(prev => ({
          ...prev,
          isSubmitting: false,
          response: data.message || "Erro ao processar cancelamento",
          responseStatus: "error"
        }));
      }
    } catch (err) {
      console.error("Erro ao cancelar assinatura:", err);
      setCancellationModal(prev => ({
        ...prev,
        isSubmitting: false,
        response: "Erro ao conectar com o servidor",
        responseStatus: "error"
      }));
    }
  };

  if (loading) {
    return (
      <div className="state">
          <div className="state-container">
            <div className="state-card">
                <h2>Carregando informações da sua conta...</h2>
                <img
                  src="/loading_lesson.png"
                  alt="Carregando informações..."
                  className="account-state-image"
                />
            </div>
          </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="state">
          <div className="state-container">
            <div className="state-card">
                <h2>Ocorreu um erro</h2>
                <img
                  src="/not_found_lesson.png"
                  alt="Ocorreu um erro"
                  className="account-state-image"
                />
            </div>
          </div>
      </div>
    );
  }
  if (!account) {
    return (
      <div className="state">
          <div className="state-container">
            <div className="state-card">
                <h2>Nenuma informação encontrada</h2>
                <img
                  src="/not_found_lesson.png"
                  alt="Nenuma informação encontrada"
                  className="account-state-image"
                />
            </div>
          </div>
      </div>
    );
  }

  return (
    <div className="account-page">
      <div className="account-page-inner">
        <h1 className="account-page-title">Sua conta</h1>

        <div className="account-hero">
          <div className="account-hero-cover" />
          <div className="account-hero-photo">
            <img
              src={account.profile_photo && !imageError ? account.profile_photo : default_profile_image_url}
              alt="Foto de perfil"
              className="account-hero-img"
              onError={() => setImageError(true)}
            />
          </div>
          <h2 className="account-hero-name">{account.full_name || "—"}</h2>
          {account.slug && <p className="account-hero-handle">@{account.slug}</p>}
          <div className="account-hero-badge">
            <AccountSpecificMessage account_type={account.account_type} />
          </div>
          <div className="account-hero-actions">
            <a className="account-action-btn" href="/account-edit">
              <Pencil size={16} /> Editar informações públicas
            </a>
            <a className="account-action-btn account-action-btn-secondary" href={`/user/${account.slug}`}>
              <Eye size={16} /> Ver página pública
            </a>
          </div>
        </div>

        <div className="account-info-section">
          <h3 className="account-info-title">Informações da conta</h3>

          <div className="account-info-card">
            <div className="account-info-item">
              <div className="account-info-header">
                <User size={18} />
                <strong>Nome</strong>
              </div>
              <div className="account-info-content">
                {account.full_name || "—"}
              </div>
            </div>

            <div className="account-info-item">
              <div className="account-info-header">
                <Mail size={18} />
                <strong>Email</strong>
              </div>
              <div className="account-info-content">
                {account.email || "—"}
              </div>
            </div>

            <div className="account-info-item">
              <div className="account-info-header">
                <FileText size={18} />
                <strong>Biografia</strong>
              </div>
              <div className="account-info-content">
                {account.biography || "(sem biografia cadastrada)"}
              </div>
            </div>

            <div className="account-info-item">
              <div className="account-info-header">
                <Trophy size={18} />
                <strong>Aulas concluídas</strong>
              </div>
              <div className="account-info-content">
                <span className="info-badge">
                  {countCompletedLessons(account.completed_lessons)}
                </span>
              </div>
            </div>

            <div className="account-info-item">
              <div className="account-info-header">
                <BookOpen size={18} />
                <strong>Cursos inscritos</strong>
              </div>
              <div className="account-info-content">
                <span className="info-badge">
                  {Array.isArray(account.courses_enrolled)
                    ? account.courses_enrolled.length
                    : "—"}
                </span>
              </div>
            </div>

            {account.link && (
              <div className="account-info-item">
                <div className="account-info-header">
                  <LinkIcon size={18} />
                  <strong>Link</strong>
                </div>
                <div className="account-info-content">
                  <a href={account.link} target="_blank" rel="noopener noreferrer">
                    {account.link}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="subscriptions-section">
          <h3 className="subscriptions-title">
            Assinaturas e pagamentos
          </h3>

          {!account.subscriptions || account.subscriptions.length === 0 ? (
            <div className="subscription-empty">
              Nenhuma assinatura encontrada.
            </div>
          ) : (
            account.subscriptions.map((subscription, index) => (
              <div
                key={index}
                className="subscription-card"
              >
                <div className="subscription-header">
                  <div>
                    <strong>
                      #{account.subscriptions.length - index}
                    </strong>
                  </div>

                  <span
                    className={`status-badge subscription-status-${String(
                      subscription.status
                    ).toLowerCase()}`}
                  >
                    {getSubscriptionStatusLabel(subscription.status)}
                  </span>
                </div>

                <div className="subscription-details">

                  <div className="subscription-summary-row">

                    <span className="money-badge">
                      {formatBRL(subscription.value)}
                    </span>

                    <span className="info-badge">
                      {getCycleLabel(subscription.cycle)}
                    </span>

                    <span className="info-badge">
                      {getBillingTypeLabel(subscription.billing_type)}
                    </span>

                  </div>

                  <div>
                    <strong>Provedor:</strong>{" "}
                    {subscription.provider || "—"}
                  </div>

                  <div className="subscription-meta-row">
                    <div>
                      <strong>Data de criação:</strong>{" "}
                      {formatDate(subscription.created_at)}
                    </div>
                    {(canCancelWithRefund(subscription) || canCancelRenewal(subscription)) && (
                      <button
                        className="cancel-link"
                        onClick={() => openCancellationModal(index)}
                      >
                        Cancelar
                      </button>
                    )}
                  </div>

                </div>

                <div className="payments-section">
                  <h4>Pagamentos</h4>

                  {!subscription.payments ||
                  subscription.payments.length === 0 ? (
                    <div className="payment-empty">
                      Nenhum pagamento encontrado.
                    </div>
                  ) : (
                    subscription.payments.map((payment, paymentIndex) => (
                      <div
                        key={paymentIndex}
                        className="payment-card"
                      >
                        <div className="payment-header">
                          <span>
                            #{subscription.payments.length - paymentIndex}
                          </span>

                          <span
                            className={`status-badge payment-status-${String(
                              payment.status
                            ).toLowerCase()}`}
                          >
                            {getPaymentStatusLabel(payment.status)}
                          </span>
                        </div>

                        <div className="payment-details">
                          <div className="payment-summary-row">

                            <span className="money-badge">
                              {formatBRL(payment.value)}
                            </span>

                            {subscription.billing_type === "CREDIT_CARD" && (
                              <span className="info-badge">
                                {payment.num_insts > 1
                                  ? `Parcelado em ${payment.num_insts}x`
                                  : "À vista"}
                              </span>
                            )}

                          </div>

                          <div>
                            <strong>Data:</strong>{" "}
                            {formatDate(payment.created_at)}
                          </div>

                          {payment.invoice_url && (
                            <div>
                              <strong>Fatura:</strong>{" "}
                              <a
                                href={payment.invoice_url}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Abrir fatura
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {cancellationModal.isOpen && (
        <div className="cancellation-modal-overlay">
          <div className="cancellation-modal">
            <div className="cancellation-modal-header">
              <h3>Cancelar assinatura</h3>
              <button
                className="cancellation-modal-close"
                onClick={closeCancellationModal}
              >
                <X size={20} />
              </button>
            </div>

            <div className="cancellation-modal-body">
              {cancellationModal.response ? (
                <div className={`cancellation-response cancellation-response-${cancellationModal.responseStatus}`}>
                  <p>{cancellationModal.response}</p>
                </div>
              ) : (
                <>
                  <div className="cancellation-option">
                    <label className="cancellation-radio-label">
                      <input
                        type="radio"
                        name="cancellation-type"
                        value="full_refund"
                        checked={cancellationModal.selectedType === "full_refund"}
                        onChange={() => handleCancellationTypeChange("full_refund")}
                        disabled={!canCancelWithRefund(account.subscriptions[cancellationModal.subscriptionIndex])}
                      />
                      <span><b>Cancelamento do período atual com reembolso total:</b> nesta opção você perde acesso ao conteúdo premium imediatamente e solicitamos ao provedor de pagamento o reembolso total do seu pagamento.</span>
                    </label>
                    {!canCancelWithRefund(account.subscriptions[cancellationModal.subscriptionIndex]) && (
                      <p className="cancellation-disabled-reason">
                        Disponível apenas para assinaturas com menos de {REFUND_DAYS} dias
                      </p>
                    )}
                  </div>

                  <div className="cancellation-option">
                    <label className="cancellation-radio-label">
                      <input
                        type="radio"
                        name="cancellation-type"
                        value="cancel_renewal"
                        checked={cancellationModal.selectedType === "cancel_renewal"}
                        onChange={() => handleCancellationTypeChange("cancel_renewal")}
                        disabled={!canCancelRenewal(account.subscriptions[cancellationModal.subscriptionIndex])}
                      />
                      <span><b>Cancelar renovação da assinatura:</b> nesta opção você mantém o seu acesso ao conteúdo premium até o final do perído contratado, mas você cancela a cobrança futura referente ao próximo período.</span>
                    </label>
                    {!canCancelRenewal(account.subscriptions[cancellationModal.subscriptionIndex]) && (
                      <p className="cancellation-disabled-reason">
                        {account.subscriptions[cancellationModal.subscriptionIndex]?.cycle === "UNIQUE"
                          ? "Não disponível para assinaturas únicas"
                          : account.subscriptions[cancellationModal.subscriptionIndex]?.billing_type === "CREDIT_CARD" &&
                            account.subscriptions[cancellationModal.subscriptionIndex]?.payments?.[0]?.num_insts > 1
                          ? "Não disponível para assinaturas parceladas (assinaturas parceladas no cartão de crédito não têm renovação automática)"
                          : "Não disponível para esta assinatura"}
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="cancellation-modal-footer">
              {cancellationModal.response ? (
                <button
                  className="cancellation-confirm-btn"
                  onClick={closeCancellationModal}
                >
                  Fechar
                </button>
              ) : (
                <button
                  className={`cancellation-confirm-btn ${
                    !cancellationModal.selectedType ? "disabled" : ""
                  }`}
                  onClick={handleCancelSubscription}
                  disabled={!cancellationModal.selectedType || cancellationModal.isSubmitting}
                >
                  {cancellationModal.isSubmitting ? "Processando..." : "Confirmar"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Account;
